import type { GetStaticPaths, GetStaticProps } from "next"
import _sortBy from "lodash/sortBy"

import { getArticle } from "~/api/prismic/articles"
import { DocumentType } from "~/api/prismic/enums"
import { queryDocumentUIDsByType } from "~/api/prismic/graphql/documents/queryDocumentsByType"
import type { Category, DefinedContentRelationship } from "~/api/prismic/types"
import type { ArticlePathParams, ArticleProps } from "~/screens/ArticlePage"
import { ArticlePage } from "~/screens/ArticlePage"
import { withLoadingFallback } from "~/utils/hocs/withLoadingFallback"
import { getArticleLink } from "~/utils/links/getArticleLink"
import { enhanceStaticProps } from "~/utils/next/enhanceStaticProps"
import { getImageDetails } from "~/utils/prismic/assertJsonTypes"
import { getPreviewRef } from "~/utils/prismic/previewData"
import { asHTML, asText } from "~/utils/prismic/richText"
import { getRelatedColorSchemeId } from "~/utils/style/colorSchemes"
import { withoutFalsyValues } from "~/utils/withoutFalsyValues"

export const getStaticPaths: GetStaticPaths<{ uid: string }> = async () => {
  const uids = await queryDocumentUIDsByType({
    pageSize: 20,
    type: DocumentType.ARTICLE,
  })

  return {
    paths: uids.map((uid) => ({ params: { uid } })),
    // Always pre-render new articles
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<ArticleProps, ArticlePathParams> =
  enhanceStaticProps<ArticleProps, ArticlePathParams>(
    async ({ params, previewData }) => {
      const uid = params && params.uid

      if (!uid) {
        return { notFound: true }
      }

      const article = await getArticle({
        uid,
        ref: getPreviewRef(previewData),
      })

      if (!article) {
        return { notFound: true }
      }

      const articlePageProps: ArticleProps = {
        articleId: article.id,
        categoryIds: article.data.categories
          .map((c) => c.category_link)
          .filter((c): c is DefinedContentRelationship<Category> => "id" in c)
          .map((c) => c.id),
        uid,
        path: getArticleLink({ uid }),
        title: asText(article.data.title),
        subtitle: article.data.subtitle,
        meta: {
          description:
            article.data.meta_description ||
            asText(article.data.subtitle || []),
        },
        date: article.first_publication_date,
        colorSchemeId: getRelatedColorSchemeId(article.data.color_scheme),
        audioURL: article.data.audio_url,
        bannerImage: getImageDetails(article.data.banner_image),
        bannerImageCredits:
          article.data.banner_image_credits &&
          asHTML(article.data.banner_image_credits),
        slices: article.data.body,
        authors: withoutFalsyValues(
          [
            ...article.data.authors,
            article.data.author && { link: article.data.author },
          ].map((author) => {
            if (!author) return null
            if (!("id" in author.link)) return null

            const { link } = author

            if (!link.data?.name || !link.uid) return null

            return {
              name: link.data.name,
              twitter: link.data.twitter_handle,
              instagram: link.data.instagram,
              uid: link.uid,
            }
          }),
        ),
        footnotes: _sortBy(article.data.footnotes, "number").filter(
          (n) => n.number,
        ),
      }

      return {
        props: articlePageProps,
      }
    },
  )

export default withLoadingFallback(ArticlePage)
